<!--
 * @Author: Tahiti
 * @Date: 2025-10-20 19:34:50
 * @LastEditors: Tahiti
 * @LastEditTime: 2025-12-27 12:00:00
 * @Description: 
 * @FilePath: /weryai-web/layouts/tool.vue
-->
<template>
    <div class="tool-layout w-full min-w-0 lg:flex lg:flex-col lg:h-[100dvh] lg:overflow-hidden">
        <ClientOnly>
            <!-- <MobileHomeHeader /> -->
            <NewAppHeader />
            <template #fallback>
                <AppHeaderFallback />
            </template>
        </ClientOnly>
        <tool-project-tab></tool-project-tab>
        <div ref="layoutSplitRef" class="tool-layout-split flex flex-row flex-1 min-h-0 min-w-0 w-full overflow-x-hidden"
            :class="{ dragging: isSidebarDragging }">
            <main class="tool-layout-main flex flex-1 flex-col min-w-0 bg-[#000000]" ref="toolMainRef" :class="[
                isLayoutLoggedIn ? 'lg:overflow-hidden overflow-hidden' : 'lg:overflow-y-auto lg:overflow-x-hidden overflow-y-auto overflow-x-hidden',
                { 'force-scroll-y': forceOuterScroll },
            ]">
                <div class="relative z-[1] min-h-0 min-w-0 w-full max-w-full lg:flex-1">
                    <slot />
                </div>
            </main>

            <template v-if="isLayoutLoggedIn && isProjectSidebarVisible">
                <button type="button" class="tool-layout-resize-handle hidden lg:flex" aria-label="Resize right sidebar"
                    @pointerdown="startSidebarResize">
                    <span class="tool-layout-resize-handle__grip"></span>
                </button>

                <div class="tool-right-sidebar-shell hidden lg:flex" :style="rightSidebarShellStyle">
                    <div v-if="sidebarType === 'chat'" class="tool-right-sidebar-toolbar">
                        <button v-if="sidebarType === 'chat'" class="system-prompt-entry-btn"
                            @click="handleOpenSystemPromptFromToolLayout">
                            <span class="i-prompt-tip-icon"></span>
                        </button>
                    </div>

                    <div class="tool-right-sidebar-panel">
                        <ChatSidebar ref="chatSidebarRef" class="tool-chat-sidebar" stretch-to-parent
                            :visible="!appStore.isSmallScreen && isProjectSidebarVisible" :type="sidebarType"
                            :prevent-navigation="sidebarType === 'chat'" @toggle="toggleProjectSidebar"
                            @close="isProjectSidebarVisible = false" @select="handleSidebarSelect"
                            @new-chat="handleSidebarNewChat" @conversation-deleted="handleSidebarConversationDeleted" />
                    </div>
                </div>
            </template>

            <button v-else-if="isLayoutLoggedIn" class="sidebar-expand-btn hidden lg:flex" @click="toggleProjectSidebar">
                <img src="@/assets/images/common/chat-slidebar-icon.png" alt="open sidebar" class="w-5 h-5" />
            </button>

            <ChatSidebar v-if="isLayoutLoggedIn" :visible="appStore.isSmallScreen && isProjectSidebarVisible"
                :type="sidebarType" :prevent-navigation="sidebarType === 'chat'" @toggle="toggleProjectSidebar"
                @close="isProjectSidebarVisible = false" @select="handleSidebarSelect" @new-chat="handleSidebarNewChat"
                @conversation-deleted="handleSidebarConversationDeleted" />
        </div>
    </div>

    <tool-single-img-view></tool-single-img-view>

    <!-- 系统提示词抽屉（Tool Layout, PC） -->
    <ClientOnly>
        <el-drawer v-model="systemPromptDrawerVisible" direction="rtl" :show-close="false" :close-on-click-modal="true"
            :close-on-press-escape="true" :with-header="false" size="420px" class="system-prompt-drawer"
            modal-class="system-prompt-overlay" :append-to-body="true" :teleported="true" :z-index="100000">
            <SystemPromptEditor v-model="systemPromptDraft" :title="t('chat.systemPromptTitle')"
                :subtitle="t('chat.systemPromptSubtitle')" :placeholder="t('chat.systemPromptPlaceholder')"
                :saving="systemPromptSaving" @close="systemPromptDrawerVisible = false"
                @save="handleSystemPromptSave" />
        </el-drawer>
    </ClientOnly>

    <!-- PWA 添加到桌面提示卡片（全局） -->
    <ClientOnly>
        <ToolPwaPromptCard />
    </ClientOnly>
</template>

<script setup lang="ts">
import { useAppStore } from '@/stores/app'
import { useUserStore } from '@/stores/user'
import { useChatDialogueStore } from '~/stores/chatDialogue'
import { usePwaPrompt } from '~/composables/usePwaPrompt'
import { useI18n } from '@/composables/useI18n'
import SystemPromptEditor from '@/components/chat/SystemPromptEditor.vue'
import PcWorkbench from '@/components/Tool/PcWorkbench.vue'
import ChatSidebar from '@/components/chat/ChatSidebar.vue'
import { useResizablePane } from '~/composables/useResizablePane'

const appStore = useAppStore()
const userStore = useUserStore()
const chatDialogueStore = useChatDialogueStore()
const { initBeforeInstallPrompt } = usePwaPrompt()
const { t } = useI18n()

// 判断是否登录
const isLoggedIn = computed(() => userStore.isLoggedIn)
// 用户态由客户端插件从 localStorage 恢复，不能在 hydration 前参与布局分支。
const isAuthLayoutReady = ref(false)
const isLayoutLoggedIn = computed(
    () => isAuthLayoutReady.value && isLoggedIn.value
)

// 匿名工具页空状态会在异步历史组件挂载后成为 LCP，提前从 SSR head 发起请求。
useHead(() =>
    isLayoutLoggedIn.value
        ? {}
        : {
              link: [
                  {
                      rel: 'preload',
                      as: 'image',
                      href: 'https://static.weryai.com/growth/image/48b2bf9f0fcba0cdd7c00550a00b978b.webp',
                      fetchpriority: 'high',
                  },
              ],
          }
)

// ==================== 侧边栏状态 ====================
const isMobile = () => typeof window !== 'undefined' && window.innerWidth < 1024
// SSR 与客户端首次渲染统一保留节点，挂载后再按真实 viewport 调整。
const isProjectSidebarVisible = ref(true)
const chatSidebarRef = ref<InstanceType<typeof ChatSidebar> | null>(null)
const layoutSplitRef = ref<HTMLElement | null>(null)
const mainPaneWidthPercent = computed({
    get: () => 100 - appStore.toolRightSidebarWidthPercent,
    set: value => {
        appStore.setToolRightSidebarWidthPercent(100 - value)
    },
})
const { isDragging: isSidebarDragging, startDragging: startSidebarResize } =
    useResizablePane({
        containerRef: layoutSplitRef,
        sizePercent: mainPaneWidthPercent,
        minPanePx: 680,
        minContentPx: 308,
    })
const rightSidebarShellStyle = computed(() => ({
    '--tool-right-sidebar-width': `${appStore.toolRightSidebarWidthPercent}%`,
}))

// 侧边栏 type 根据 RightOutputPlate 的 activeTab 动态切换
const sidebarType = computed(() => {
    return appStore.rightOutputActiveTab === 'chat' ? 'chat' : 'project'
})

const toggleProjectSidebar = () => {
    isProjectSidebarVisible.value = !isProjectSidebarVisible.value
}

// ==================== 系统提示词抽屉（Tool Layout, no navigation） ====================
const systemPromptDrawerVisible = ref(false)
const systemPromptDraft = ref('')
const systemPromptSaving = ref(false)

const handleOpenSystemPromptFromToolLayout = () => {
    if (systemPromptSaving.value) return

    // Ensure a conversation exists so we can save system prompt.
    if (!chatDialogueStore.conversationId) {
        chatDialogueStore.createConversation(true).then((ok) => {
            if (!ok) return
            chatDialogueStore.triggerSidebarRefresh()
            systemPromptDraft.value = chatDialogueStore.systemPrompt || ''
            systemPromptDrawerVisible.value = true
        })
        return
    }

    systemPromptDraft.value = chatDialogueStore.systemPrompt || ''
    systemPromptDrawerVisible.value = true
}

const handleSystemPromptSave = async () => {
    if (systemPromptSaving.value) return
    if (!chatDialogueStore.conversationId) return

    systemPromptSaving.value = true
    try {
        const value = (systemPromptDraft.value ?? '').trim()
        if (value === (chatDialogueStore.systemPrompt || '')) {
            systemPromptDrawerVisible.value = false
            return
        }

        const { updateConversationNameApi } = await import('~/api/chat')
        const res = await updateConversationNameApi(String(chatDialogueStore.conversationId), {
            update_type: 'SYSTEM_PROMPT',
            system_prompt: value,
        })
        if (res?.status === 200 || res?.success) {
            chatDialogueStore.systemPrompt = value
            chatDialogueStore.triggerSidebarRefresh()
            chatDialogueStore.triggerCreditsRefresh()
        }
        systemPromptDrawerVisible.value = false
    } catch (e) {
        console.error('[ToolLayout] save system prompt failed:', e)
    } finally {
        systemPromptSaving.value = false
    }
}

watch(
    () => sidebarType.value,
    (type) => {
        if (type !== 'chat' && systemPromptDrawerVisible.value) {
            systemPromptDrawerVisible.value = false
        }
    }
)

const handleSidebarSelect = (item: any, type: string) => {
    if (type === 'project' && item?.id) {
        appStore.changeCurrentProject(item)
    } else if (type === 'chat' && item?.id) {
        chatDialogueStore.setConversationId(String(item.id))
    }
}

const handleSidebarNewChat = () => {
    // ChatSidebar 已内部完成创建，此处无需额外操作
}

const handleSidebarConversationDeleted = () => {
    // ChatSidebar 已内部完成 initNewConversation，此处无需额外操作
}

// 通过 Pinia store 响应 ChatPanel 的侧边栏刷新请求（替代 emitter 事件总线）
watch(
    () => chatDialogueStore.sidebarRefreshTrigger,
    () => {
        if (sidebarType.value === 'chat') {
            chatSidebarRef.value?.loadChatHistory(false, true)
        }
    }
)

// 登录后：将工具页外层滚动容器 smooth 回到顶部
const toolMainRef = ref<HTMLElement | null>(null)
const forceOuterScroll = ref(false)
let forceScrollTimer: number | null = null
let onForceScroll: (() => void) | null = null

const stopForceOuterScroll = () => {
    forceOuterScroll.value = false
    if (import.meta.client && forceScrollTimer != null) {
        window.clearTimeout(forceScrollTimer)
        forceScrollTimer = null
    }
    const el = toolMainRef.value
    if (el && onForceScroll) {
        el.removeEventListener('scroll', onForceScroll)
    }
    onForceScroll = null
}

watch(
    isLoggedIn,
    async (val, oldVal) => {
        // only on: not logged in -> logged in
        if (!val || oldVal) return
        if (!import.meta.client) return

        const el = toolMainRef.value
        if (!el) return
        if (el.scrollTop <= 0) return

        // Ensure the outer container is scrollable for the duration of smooth scroll,
        // even though logged-in state uses overflow-hidden on desktop.
        stopForceOuterScroll()
        forceOuterScroll.value = true

        await nextTick()
        const target = toolMainRef.value
        if (!target) {
            stopForceOuterScroll()
            return
        }

        try {
            target.scrollTo({ top: 0, behavior: 'smooth' })
        } catch {
            target.scrollTop = 0
        }

        onForceScroll = () => {
            const t = toolMainRef.value
            if (!t) return
            if (t.scrollTop <= 0) stopForceOuterScroll()
        }
        target.addEventListener('scroll', onForceScroll, { passive: true })

        // Safety: in case scroll event doesn't fire (e.g. layout changes), force reset.
        forceScrollTimer = window.setTimeout(() => {
            const t = toolMainRef.value
            if (t) t.scrollTop = 0
            stopForceOuterScroll()
        }, 1400)
    },
    // Run before DOM patch so we can capture the pre-login scrollTop.
    { flush: 'pre' }
)

onMounted(() => {
    const smallScreen = isMobile()
    appStore.setIsSmallScreen(smallScreen)
    isProjectSidebarVisible.value = !smallScreen
    isAuthLayoutReady.value = true
    appStore.hydrateWorkbenchPaneState()
    initBeforeInstallPrompt()
})

onUnmounted(() => {
    stopForceOuterScroll()
})
</script>

<style lang="less" scoped>
.tool-layout { min-height: 100dvh; }

.tool-layout-split {
    align-items: stretch;
}

/* 主列用 0% flex-basis，避免子树 min-content 宽度把右侧「我的项目」挤出视口右缘 */
.tool-layout-main {
    flex: 1 1 0%;
    min-width: 0;
}


.force-scroll-y {
    overflow-y: auto !important;
    overflow-x: hidden !important;
}

.tool-layout-resize-handle {
    width: 12px;
    flex: 0 0 12px;
    display: flex;
    align-items: center;
    justify-content: center;
    padding: 0;
    border: none;
    background: transparent;
    cursor: col-resize;
}

.tool-layout-resize-handle__grip {
    width: 4px;
    height: 72px;
    border-radius: 999px;
    background: rgba(255, 255, 255, 0.1);
    transition: background-color 0.2s ease;
}

.tool-layout-split.dragging .tool-layout-resize-handle__grip,
.tool-layout-resize-handle:hover .tool-layout-resize-handle__grip {
    background: rgba(153, 247, 104, 0.75);
}

.tool-right-sidebar-shell {
    --toolbar-width: 48px;
    /*
     * 宽度一直是「store 里的百分比 → --tool-right-sidebar-width」，不是今天才改成 %。
     * 纯 % 在超宽屏下像素会膨胀；用 clamp 在默认小占比时贴近「最小宽度」(240px)、向上不超过 380px。
     */
    flex: 0 0 clamp(240px, var(--tool-right-sidebar-width), 380px);
    width: clamp(240px, var(--tool-right-sidebar-width), 380px);
    min-width: 240px;
    flex-shrink: 0;
    display: flex;
    align-items: stretch;
    min-height: 0;
}

.tool-right-sidebar-toolbar {
    width: var(--toolbar-width);
    flex: 0 0 var(--toolbar-width);
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 12px;
    padding-top: 30px;
}

.tool-right-sidebar-panel {
    flex: 1 1 auto;
    min-width: 0;
    min-height: 0;
}

.tool-right-sidebar-panel :deep(.chat-sidebar) {
    width: 100%;
}

.tool-workbench-wrapper {
    position: fixed;
    left: 0;
    right: 0;
    bottom: 18px;
    display: flex;
    justify-content: center;
    padding: 0 16px;
    z-index: 100;
    /* keep dropdowns above tool content */
    pointer-events: none;
    /* allow page interactions outside workbench */
}

.tool-workbench-wrapper :deep(.workbench-container) {
    pointer-events: auto;
}

/* 侧边栏收起时的展开按钮 —— 垂直对齐 RightOutputPlate 的筛选行 */
.sidebar-expand-btn {
    align-items: center;
    justify-content: center;
    width: 40px;
    height: 40px;
    flex-shrink: 0;
    align-self: flex-start;
    /* output-plate padding-top(20px) + header margin-top(10px) = 30px */
    margin-top: 30px;
    background: rgba(28, 28, 32, 0.8);
    border: 1px solid rgba(255, 255, 255, 0.06);
    border-radius: 10px;
    cursor: pointer;
    transition: all 0.2s ease;

    &:hover {
        background: rgba(38, 38, 42, 0.9);
        border-color: rgba(255, 255, 255, 0.1);
    }
}

/* 系统提示词入口按钮：与 sidebar-expand-btn 同风格/尺寸 */
.system-prompt-entry-btn {
    align-items: center;
    justify-content: center;
    width: 40px;
    height: 40px;
    flex-shrink: 0;
    align-self: flex-start;
    margin-top: 30px;
    background: rgba(28, 28, 32, 0.8);
    border: 1px solid rgba(255, 255, 255, 0.06);
    border-radius: 10px;
    cursor: pointer;
    transition: all 0.2s ease;

    &:hover {
        background: rgba(38, 38, 42, 0.9);
        border-color: rgba(255, 255, 255, 0.1);
    }
}

.i-prompt-tip-icon {
    display: inline-block;
    width: 20px;
    height: 20px;
    mask: url('@/assets/images/common/prompt-tip.svg') no-repeat center;
    mask-size: contain;
    -webkit-mask: url('@/assets/images/common/prompt-tip.svg') no-repeat center;
    -webkit-mask-size: contain;
    background-image: none;
    background-color: currentColor;
}

/* Teleported drawer uses the shared header geometry. */
@media (min-width: 1024px) {
    :deep(.el-overlay.system-prompt-overlay) {
        top: var(--app-header-bottom) !important;
        height: var(--app-content-height) !important;
    }

    :deep(.el-overlay.system-prompt-overlay .el-drawer) {
        height: 100% !important;
    }
}

/* Keep Element Plus drawer body consistent with chat */
:deep(.system-prompt-drawer .el-drawer__body) {
    padding: 0;
    background-color: #101012;
}
</style>
